/**
 * The portal's administration console — a dedicated page, not a drawer.
 *
 * Three real sections move here from the old `SettingsDrawer` UNCHANGED:
 * Home Page (`HomePageEditorTab`), User Management (`UserManagementTab`) and
 * Roles & Permissions (`UserRolesTab`) — same components, same permission
 * gates, just reached through this page's own left-hand navigation instead of
 * a row of top tabs. The personal "Account" tab that used to live alongside
 * them moved OUT to its own Profile page (`ProfilePage`), because it was
 * never really an administration section — it was the one tab a normal user
 * could see, sitting among tabs they couldn't.
 *
 * Everything else here is a placeholder: the navigation entry and the page
 * exist so this console reads as complete, but nothing behind it is wired to
 * a backend yet — see PlaceholderSection.
 *
 * Structured like Esri Portal's own Organization Settings (the reference for
 * this layout): categorised left navigation, one section's content on the
 * right.
 *
 * Every signed-in user reaches this page now, not just an admin (see
 * UserMenu's `showSettings`) — but what they see inside it is narrower: a
 * section marked `everyone: true` below (My Space, Organization, User
 * Groups, ArcGIS Servers, GeoServer) is visible to any signed-in user,
 * everything else stays gated on its own `permission` or, absent one, on
 * `isAdmin`. Organization is `everyone: true` on purpose, not permission-
 * gated: what it actually SHOWS is what does the real narrowing — only
 * items an owner (or admin) has explicitly turned "Share with Organization"
 * on for, resolved server-side in `_visible_modules`/the layer-org-share
 * listings, same as My Space narrows to ownership. (An earlier version of
 * this gated the whole SECTION on `modules:view-all` instead, back when
 * that permission meant "see literally everything" — once sharing became
 * per-item or opt-in, a permission-gated nav entry on top of it just hid
 * the tab from everyone who didn't also hold that separate permission.) The
 * nav itself is filtered to `isAllowed` sections so a non-admin's sidebar
 * simply doesn't list what they can't open — the "You do not have access"
 * message below is defence-in-depth for the one case client-side filtering
 * can't cover, a typed-in `?settings=` URL. Real sections re-check their
 * own specific permission regardless (e.g. `ContentSpaceTab`/
 * `ServerConnectionsTab` gate per-row actions by ownership or `isAdmin` on
 * top of just being reachable); placeholders check the role directly, since
 * there is no finer permission for them yet.
 */

import { useMemo, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeft,
  Cloud,
  Cog,
  Database,
  Home,
  Image as ImageIcon,
  Layers,
  Lock,
  Map as MapIcon,
  Menu as MenuIcon,
  MapPin,
  Plug,
  Server,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  Users,
  UsersRound,
  X,
} from 'lucide-react';
import type { AppModule, HomePageConfig } from '../../types';
import { useAuth, usePermissions } from '../../auth/AuthContext';
import type { Permission } from '../../auth/permissions';
import { roleService } from '../../services/roleService';
import GeneralSettingsTab from './GeneralSettingsTab';
import HomePageEditorTab from './HomePageEditorTab';
import ContentSpaceTab from './ContentSpaceTab';
import ServerConnectionsTab from './ServerConnectionsTab';
import UserManagementTab from './UserManagementTab';
import UserRolesTab from './UserRolesTab';
import UserGroupsTab from './UserGroupsTab';
import PlaceholderSection from './PlaceholderSection';

interface SectionDefinition {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Undefined = gated on `isAdmin` alone, the placeholder default. */
  permission?: Permission;
  /** True = open to any signed-in user, admin or not — takes priority over
   *  the `isAdmin` default (never combined with `permission`, which already
   *  has its own gate). */
  everyone?: boolean;
  placeholder?: { description: string };
}

interface CategoryDefinition {
  label: string;
  sections: SectionDefinition[];
}

const CATEGORIES: CategoryDefinition[] = [
  {
    label: 'General',
    sections: [
      {
        // Admin-only, deliberately NOT `permission: 'home:edit'` — portal
        // branding and the home page's own layout are a single admin's
        // call, not something a custom role (even one granted `home:edit`
        // for the inline "edit this home page" affordance on the portal
        // page itself) should reach through Settings too.
        id: 'general',
        label: 'General',
        icon: Settings2,
      },
      { id: 'home', label: 'Home Page', icon: Home },
      { id: 'mySpace', label: 'My Space', icon: ImageIcon, everyone: true },
      { id: 'organization', label: 'Organization', icon: Database, everyone: true },
    ],
  },
  {
    label: 'BlueView',
    sections: [
      {
        id: 'mapConfig',
        label: 'Map Configuration',
        icon: MapIcon,
        placeholder: { description: 'Portal-wide defaults for a new map — starting extent, tools and zoom limits.' },
      },
      {
        id: 'basemapConfig',
        label: 'Basemap Configuration',
        icon: Layers,
        placeholder: { description: 'Which basemaps admins may offer, and which one is selected by default.' },
      },
      {
        id: 'layerConfig',
        label: 'Layer Configuration',
        icon: SlidersHorizontal,
        placeholder: { description: 'Default symbology, labelling and popup behaviour for newly added layers.' },
      },
      {
        id: 'appSettings',
        label: 'Application Settings',
        icon: Cog,
        placeholder: { description: 'Portal-wide toggles for the Builder and the published Tool Library.' },
      },
    ],
  },
  {
    label: 'Esri / GIS',
    sections: [
      { id: 'arcgisServers', label: 'ArcGIS Servers', icon: Server, everyone: true },
      { id: 'geoServer', label: 'GeoServer', icon: Cloud, everyone: true },
    ],
  },
  {
    label: 'User & Access',
    sections: [
      { id: 'users', label: 'User Management', icon: Users, permission: 'users:manage' },
      { id: 'roles', label: 'Roles & Permissions', icon: ShieldCheck, permission: 'roles:manage' },
      { id: 'userGroups', label: 'User Groups', icon: UsersRound, everyone: true },
      {
        id: 'security',
        label: 'Security',
        icon: Lock,
        placeholder: { description: 'Password policy, session lifetime and sign-in restrictions for the portal.' },
      },
    ],
  },
  {
    label: 'System',
    sections: [
      {
        id: 'locationTracking',
        label: 'Location Tracking',
        icon: MapPin,
        placeholder: { description: 'Configure how field devices report their location back to the portal.' },
      },
      {
        id: 'integrations',
        label: 'Integrations',
        icon: Plug,
        placeholder: { description: 'Connect BlueView to other systems — webhooks, SSO, and third-party APIs.' },
      },
      {
        id: 'systemConfig',
        label: 'System Configuration',
        icon: Cog,
        placeholder: { description: 'Server-level settings: storage, backups and maintenance windows.' },
      },
    ],
  },
];

const ALL_SECTIONS = CATEGORIES.flatMap((category) => category.sections);

/** Sections with a real, built screen behind them — wrapped in the white
 *  content card below. A placeholder already draws its own dashed empty
 *  state (see PlaceholderSection); boxing that a second time would read as
 *  a card inside a card rather than "coming soon". */
const BUILT_SECTION_IDS = new Set([
  'general', 'home', 'mySpace', 'organization', 'arcgisServers', 'geoServer', 'users', 'roles', 'userGroups',
]);

export default function SettingsPage({
  section,
  onSectionChange,
  onBack,
  homeConfig,
  onHomeConfigSaved,
  modules,
  onOpenModule,
  onEditModule,
  onShareModule,
  onDeleteModule,
  onModulesChanged,
}: {
  /** The section id from the URL — see Portal's `settings` query param. */
  section: string;
  onSectionChange: (section: string) => void;
  onBack: () => void;
  homeConfig: HomePageConfig;
  onHomeConfigSaved: (config: HomePageConfig) => void;
  /** Every module the caller may see — for an admin (the only one who
   *  reaches this page), that is already every module in the portal. */
  modules: AppModule[];
  /** Opens a module however is appropriate for what the caller can do with
   *  it — the read-only viewer for anyone who cannot edit it (including a
   *  draft), the Builder otherwise. The SAFE default for "open this" from
   *  My Space/Organization; `onEditModule` below is the strict, edit-only
   *  action and refuses outright rather than falling back. */
  onOpenModule: (id: string) => void;
  onEditModule: (id: string) => void;
  onShareModule: (id: string) => void;
  onDeleteModule: (id: string) => void;
  onModulesChanged: (modules: AppModule[]) => void;
}) {
  const { currentUser } = useAuth();
  const { can, isAdmin } = usePermissions();
  /** The left nav as a slide-over on small screens, closed by default. */
  const [navOpen, setNavOpen] = useState(false);

  const isAllowed = useMemo(
    () => (item: SectionDefinition) =>
      item.permission ? can(item.permission) : item.everyone ? true : isAdmin,
    [can, isAdmin],
  );

  // Non-admins never see BlueView/User & Access/System — filtered out below
  // by dropping categories with no allowed section left, not by disabling
  // them, so the sidebar itself never hints those sections exist.
  const visibleCategories = useMemo(
    () =>
      CATEGORIES.map((category) => ({
        ...category,
        sections: category.sections.filter(isAllowed),
      })).filter((category) => category.sections.length > 0),
    [isAllowed],
  );
  const allowedSections = useMemo(() => ALL_SECTIONS.filter(isAllowed), [isAllowed]);

  const activeItem =
    allowedSections.find((item) => item.id === section) ?? allowedSections[0] ?? ALL_SECTIONS[0];
  const ActiveIcon = activeItem.icon;

  if (!currentUser) return null;

  const selectSection = (id: string) => {
    onSectionChange(id);
    setNavOpen(false);
  };

  const nav = (
    <nav className="space-y-5">
      {visibleCategories.map((category) => (
        <div key={category.label}>
          <h3 className="px-3 text-[10.5px] font-bold uppercase tracking-wider text-slate-400">
            {category.label}
          </h3>
          <div className="mt-1.5 space-y-0.5">
            {category.sections.map((item) => {
              const Icon = item.icon;
              const activeHere = item.id === activeItem.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => selectSection(item.id)}
                  aria-current={activeHere}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[13px] font-medium transition ${
                    activeHere
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                  }`}
                >
                  <Icon className={`h-4 w-4 flex-shrink-0 ${activeHere ? 'text-blue-600' : 'text-slate-400'}`} />
                  <span className="truncate">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-slate-50">
      <header className="flex flex-shrink-0 items-center gap-3 border-b border-blue-100 bg-blue-50 px-4 py-3 sm:px-6">
        <button
          type="button"
          onClick={onBack}
          className="gl-btn gl-btn--secondary px-2.5 py-1.5 text-[12.5px]"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to the portal
        </button>
        <button
          type="button"
          onClick={() => setNavOpen(true)}
          aria-label="Open settings navigation"
          className="gl-btn gl-btn--secondary px-2 py-1.5 md:hidden"
        >
          <MenuIcon className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-sm font-semibold text-slate-800">Settings</h1>
          <p className="truncate text-[11px] text-slate-400">
            {currentUser.name} ·{' '}
            <span className="uppercase tracking-wide">{roleService.myRoleName() ?? currentUser.role}</span>
          </p>
        </div>
      </header>

      <div className="relative flex min-h-0 flex-1">
        {/* Static sidebar, medium screens and up. */}
        <aside className="hidden w-64 flex-shrink-0 overflow-y-auto border-r border-slate-200 bg-white px-3 py-5 md:block">
          {nav}
        </aside>

        {/* Small screens: the same nav as a slide-over drawer. */}
        {navOpen && (
          <div className="fixed inset-0 z-[7000] md:hidden">
            <div
              onClick={() => setNavOpen(false)}
              aria-hidden="true"
              className="gl-modal-backdrop absolute inset-0"
            />
            <aside className="gl-panel absolute left-0 top-0 h-full w-72 max-w-[85vw] overflow-y-auto px-3 py-5">
              <div className="mb-3 flex items-center justify-between px-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Settings</span>
                <button
                  type="button"
                  onClick={() => setNavOpen(false)}
                  aria-label="Close settings navigation"
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              {nav}
            </aside>
          </div>
        )}

        <main className="min-h-0 flex-1 overflow-y-auto bg-slate-50 px-4 py-6 sm:px-8">
          <div className="w-full">
            <div className="mb-5 flex items-center gap-2.5">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#0F2942]/[0.07] text-[#0F2942]">
                <ActiveIcon className="h-4.5 w-4.5" />
              </span>
              <h2 className="text-base font-semibold text-slate-800">{activeItem.label}</h2>
            </div>

            <div
              className={
                isAllowed(activeItem) && BUILT_SECTION_IDS.has(activeItem.id)
                  ? 'rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7'
                  : undefined
              }
            >
              {!isAllowed(activeItem) ? (
              <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12.5px] text-amber-800">
                You do not have access to this section.
              </p>
            ) : activeItem.id === 'general' ? (
              <GeneralSettingsTab config={homeConfig} onSaved={onHomeConfigSaved} />
            ) : activeItem.id === 'home' ? (
              <HomePageEditorTab
                config={homeConfig}
                onSaved={onHomeConfigSaved}
                modules={modules}
                onModulesChanged={onModulesChanged}
              />
            ) : activeItem.id === 'mySpace' ? (
              <ContentSpaceTab
                scope="mine"
                modules={modules}
                onOpenModule={onOpenModule}
                onEditModule={onEditModule}
                onShareModule={onShareModule}
                onDeleteModule={onDeleteModule}
                onModulesChanged={onModulesChanged}
                canShareModules={can('modules:share')}
              />
            ) : activeItem.id === 'organization' ? (
              <ContentSpaceTab
                scope="all"
                modules={modules}
                onOpenModule={onOpenModule}
                onEditModule={onEditModule}
                onShareModule={onShareModule}
                onDeleteModule={onDeleteModule}
                onModulesChanged={onModulesChanged}
                canShareModules={can('modules:share')}
              />
            ) : activeItem.id === 'arcgisServers' ? (
              <ServerConnectionsTab type="arcgis" />
            ) : activeItem.id === 'geoServer' ? (
              <ServerConnectionsTab type="geoserver" />
            ) : activeItem.id === 'users' ? (
              <UserManagementTab currentUserId={currentUser.id} />
            ) : activeItem.id === 'roles' ? (
              <UserRolesTab />
            ) : activeItem.id === 'userGroups' ? (
              <UserGroupsTab modules={modules} />
            ) : (
              <PlaceholderSection
                icon={activeItem.icon}
                title={activeItem.label}
                description={activeItem.placeholder?.description ?? 'This feature is coming soon.'}
              />
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
